import { useState, useEffect, useCallback, useRef } from "react";
import {
  getCreditNoteProcessingStatus,
  getCreditNoteSummaries,
  getGenerationRuns,
} from "../../../../services/clearing/credit-notes";
import { GenerationRun, deriveActiveRun } from "../generationRuns";

const POLL_INTERVAL_MS = 5000;

interface PendingCreditNote {
  id: string;
  station_id: string;
  customer_id: string;
  customer_name: string;
  total_net: number;
  service_start_date: string;
  service_end_date: string;
  created_at: string;
  claimed_at: string | null;
  process_id?: string;
}

export interface CreditNotesStatus {
  total_generated: number;
  pending_count: number;
  processed_count: number;
  pending: PendingCreditNote[];
  run_started_at: string | null;
  run_total: number | null;
  generating: boolean;
}

export interface CreditNoteSummaryItem {
  id: string;
  station_id: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  total_net: number;
  subtotal_net: number;
  provision_amount: number;
  service_start_date: string;
  service_end_date: string;
  created_at: string;
  easybill_document_id: number | null;
}

export interface SummariesResponse {
  data: CreditNoteSummaryItem[];
  total_items: number;
  total_pages: number;
  page: number;
  limit: number;
}

export interface RunProgress {
  total: number;
  remaining: number;
  processed: number;
  percent: number;
  finished: boolean;
  startedAt: string | null;
}

export const useCreditNotes = () => {
  const [status, setStatus] = useState<CreditNotesStatus | null>(null);
  const [summaries, setSummaries] = useState<SummariesResponse | null>(null);
  const [runs, setRuns] = useState<GenerationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [summariesPage, setSummariesPage] = useState(1);
  const [summariesLimit] = useState(20);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const justSubmittedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const fetchAll = useCallback(async (page: number, limit: number) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const [statusResult, summariesResult, runsResult] = await Promise.allSettled([
        getCreditNoteProcessingStatus(),
        getCreditNoteSummaries({ page, limit }),
        getGenerationRuns(50),
      ]);
      if (statusResult.status === "fulfilled") setStatus(statusResult.value);
      if (summariesResult.status === "fulfilled") setSummaries(summariesResult.value);
      if (runsResult.status === "fulfilled") setRuns(runsResult.value ?? []);
    } catch (err) {
      console.error("Failed to fetch processing data", err);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchAll(summariesPage, summariesLimit);
  }, [fetchAll, summariesPage, summariesLimit]);

  const markSubmitted = useCallback(() => {
    setJustSubmitted(true);
    if (justSubmittedTimer.current) clearTimeout(justSubmittedTimer.current);
    justSubmittedTimer.current = setTimeout(() => setJustSubmitted(false), 30000);
  }, []);

  // handoff: once the server confirms a run is generating, drop the local guard
  useEffect(() => {
    if (justSubmitted && status?.generating) {
      setJustSubmitted(false);
      if (justSubmittedTimer.current) clearTimeout(justSubmittedTimer.current);
    }
  }, [justSubmitted, status?.generating]);

  useEffect(() => {
    fetchAll(summariesPage, summariesLimit);

    const poll = () => {
      timeoutRef.current = setTimeout(async () => {
        await fetchAll(summariesPage, summariesLimit);
        poll();
      }, POLL_INTERVAL_MS);
    };
    poll();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (justSubmittedTimer.current) {
        clearTimeout(justSubmittedTimer.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAll, summariesPage, summariesLimit]);

  const onSummariesPageChange = useCallback((page: number) => {
    setSummariesPage(page);
  }, []);

  const runProgress: RunProgress | null = (() => {
    if (!status) return null;
    const runTotal = status.run_total;
    const startedAt = status.run_started_at;

    if (runTotal != null && runTotal > 0) {
      const remaining = status.pending_count;
      const processed = runTotal - remaining;
      const percent = Math.round((processed / runTotal) * 100);
      return { total: runTotal, remaining, processed, percent, finished: remaining === 0, startedAt };
    }

    // Fallback when no run metadata exists
    const { total_generated: total, pending_count: remaining, processed_count: processed } = status;
    const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
    return { total, remaining, processed, percent, finished: remaining === 0, startedAt: null };
  })();

  const activeRun = deriveActiveRun(runs, status?.run_started_at ?? null);

  const anyGenerating = justSubmitted || status?.generating === true;

  return { status, summaries, runs, activeRun, loading, refresh, runProgress, summariesPage, summariesLimit, onSummariesPageChange, anyGenerating, markSubmitted };
};
